/**
 * CR401: Standalone Template Editor Page
 * 
 * Route: /admin/deal-cards/templates/:deal_type_key/:variant_key
 * 
 * Supports deep-linking:
 * - ?tab=components&focus=ribbon → Opens Components tab and focuses Ribbon section
 * - ?tab=structure&focus=ribbon → Opens Structure tab and scrolls to ribbon toggle
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { previewConfigStore } from "@/stores/previewConfigStore";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Save, 
  RefreshCw,
  Undo2,
  Bug,
} from "lucide-react";
import { DealCardEditorCore } from "@/components/admin/command-center/DealCardEditorCore";
import type { 
  DealTypeRegistryEntry, 
  DealCardTemplate, 
  CardTemplateJson,
  CardTemplateSections,
} from "@/providers/DealCardConfigProvider";
import type { ExtendedCardTemplateJson, ComponentsConfig } from "@/components/admin/command-center/DealCardsTab";
import type { RibbonDraft } from "@/components/admin/command-center/RibbonSettingsPanel";
import type { HeaderComposition } from "@/components/admin/command-center/HeaderCompositionPanel";
import type { ExtendedSectionToggles } from "@/components/admin/command-center/SectionTogglesPanel";
import type { CardStyling } from "@/components/admin/command-center/CardStylingPanel";
import { DEFAULT_HEADER_COMPOSITION } from "@/components/admin/command-center/HeaderCompositionPanel";
import { DEFAULT_EXTENDED_SECTIONS } from "@/components/admin/command-center/SectionTogglesPanel";
import { DEFAULT_CARD_STYLING } from "@/components/admin/command-center/CardStylingPanel";
import { DEFAULT_SECTION_ORDER } from "@/components/admin/command-center/SectionOrderPanel";
import { useDealCardDrafts, type DealTypeDraft } from "@/hooks/useDealCardDrafts";
import { useDealCardConfig } from "@/providers/DealCardConfigProvider";
import { useStablePreviewKey } from "@/hooks/useStablePreviewKey";
import { sanitizeDealCardJsonForV2 } from "@/lib/deal-cards/specV2/themeOwnedGuard";
import { getDefaultTemplateForType } from "@/lib/deals/templateResolver";
import type { Json } from "@/integrations/supabase/types";
import { 
  DEFAULT_PROMO_BADGES_CONFIG, 
  type PromoBadgesConfig 
} from "@/components/admin/deal-cards/PromoBadgesAccordion";
import { 
  DEFAULT_DETAILS_CONTENT_CONFIG, 
  type DetailsContentConfig 
} from "@/components/admin/deal-cards/DetailsContentAccordion";
import { prepareOverridesForSave } from "@/lib/deal-cards/specV2/effectiveConfig";
import { OverrideSourceInspector } from "@/components/admin/deal-cards/OverrideSourceInspector";
import { applyOverrideEdit, removeOverrideAtPath } from "@/lib/deal-cards/specV2/effectiveConfig";
// CR312: Stable comparison utilities
import { stableEquals, stableHash } from "@/lib/utils/stableCompare";
// CR320: Debug strip for stability verification
import { EditorStabilityDebugStrip, type EditorSourceMode, type StateChangeReason } from "@/components/admin/deal-cards/EditorStabilityDebugStrip";
// CR325: Bounce recorder + stability mode
import { logEvt } from "@/lib/debug/dealCardEditorEvents";
import { BounceLogPanel } from "@/components/admin/deal-cards/BounceLogPanel";
import { useStabilityMode } from "@/hooks/useStabilityMode";

// Default details block toggles
const DEFAULT_DETAILS_BLOCK_TOGGLES: Record<string, boolean> = {
  back_header: true,
  back_nav: true,
  terms: true,
  fine_print: true,
  source: true,
  evidence: true,
  validator: true,
  details_actions: true,
  eligibility: true,
};

// Helper to extract saved ribbon from registry
// CR326: dealTypeKey param used for self-type fallback (no BONUS bleed)
function extractSavedRibbon(regEntry: DealTypeRegistryEntry | undefined, dealTypeKey?: string): RibbonDraft {
  if (!regEntry) {
    return { ribbon_text: dealTypeKey || "DEAL", ribbon_theme: "gray", is_enabled: true };
  }
  return {
    ribbon_text: regEntry.ribbon_text,
    ribbon_theme: regEntry.ribbon_theme,
    is_enabled: regEntry.is_enabled,
  };
}

// Helper to extract surface block toggles
function extractSurfaceBlockToggles(template: DealCardTemplate | undefined): {
  primary: Record<string, boolean>;
  details: Record<string, boolean>;
} {
  const overridesJson = (template as unknown as { overrides_json?: Record<string, unknown> })?.overrides_json;
  if (overridesJson?.surfaceOverrides) {
    const so = overridesJson.surfaceOverrides as {
      primary?: { blockToggles?: Record<string, boolean> };
      details?: { blockToggles?: Record<string, boolean> };
    };
    return {
      primary: so.primary?.blockToggles || {},
      details: { ...DEFAULT_DETAILS_BLOCK_TOGGLES, ...(so.details?.blockToggles || {}) },
    };
  }
  
  const templateJson = template?.template_json as ExtendedCardTemplateJson | undefined;
  if (templateJson?.surfaceOverrides) {
    return {
      primary: templateJson.surfaceOverrides.primary?.blockToggles || {},
      details: { ...DEFAULT_DETAILS_BLOCK_TOGGLES, ...(templateJson.surfaceOverrides.details?.blockToggles || {}) },
    };
  }
  
  return { primary: {}, details: { ...DEFAULT_DETAILS_BLOCK_TOGGLES } };
}

// Helper to extract surface block order
function extractSurfaceBlockOrder(template: DealCardTemplate | undefined): {
  primary: string[];
  details: string[];
} {
  const overridesJson = (template as unknown as { overrides_json?: Record<string, unknown> })?.overrides_json;
  if (overridesJson?.surfaceOverrides) {
    const so = overridesJson.surfaceOverrides as {
      primary?: { blockOrder?: string[] };
      details?: { blockOrder?: string[] };
    };
    return {
      primary: so.primary?.blockOrder || [],
      details: so.details?.blockOrder || [],
    };
  }
  
  const templateJson = template?.template_json as ExtendedCardTemplateJson | undefined;
  if (templateJson?.surfaceOverrides) {
    return {
      primary: templateJson.surfaceOverrides.primary?.blockOrder || [],
      details: templateJson.surfaceOverrides.details?.blockOrder || [],
    };
  }
  
  return { primary: [], details: [] };
}

// Helper to extract saved template config
function extractSavedTemplate(template: DealCardTemplate | undefined) {
  const templateJson = template?.template_json as ExtendedCardTemplateJson | undefined;
  const surfaceBlockToggles = extractSurfaceBlockToggles(template);
  const surfaceBlockOrder = extractSurfaceBlockOrder(template);
  
  if (!templateJson) {
    return {
      header: DEFAULT_HEADER_COMPOSITION,
      sections: DEFAULT_EXTENDED_SECTIONS,
      labels: {},
      styling: DEFAULT_CARD_STYLING,
      sectionOrder: [...DEFAULT_SECTION_ORDER],
      themePreset: "light" as const,
      surfaceBlockToggles,
      surfaceBlockOrder,
      components: {},
      promoBadges: { ...DEFAULT_PROMO_BADGES_CONFIG },
      detailsContent: { ...DEFAULT_DETAILS_CONTENT_CONFIG },
    };
  }
  
  return {
    header: { ...DEFAULT_HEADER_COMPOSITION, ...templateJson.header },
    sections: { ...DEFAULT_EXTENDED_SECTIONS, ...templateJson.sections, ...templateJson.extended_sections },
    labels: templateJson.labels || {},
    styling: { ...DEFAULT_CARD_STYLING, ...templateJson.styling },
    sectionOrder: templateJson.section_order || [...DEFAULT_SECTION_ORDER],
    themePreset: templateJson.theme_preset || ("light" as const),
    surfaceBlockToggles,
    surfaceBlockOrder,
    components: templateJson.components || {},
    promoBadges: templateJson.promo_badges || { ...DEFAULT_PROMO_BADGES_CONFIG },
    detailsContent: templateJson.details_content || { ...DEFAULT_DETAILS_CONTENT_CONFIG },
  };
}

export default function TemplateEditorPage() {
  const { deal_type_key, variant_key } = useParams<{ deal_type_key: string; variant_key: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Deep-link params
  const deepLinkTab = searchParams.get("tab");
  const deepLinkFocus = searchParams.get("focus");
  
  // Data state
  const [registry, setRegistry] = useState<DealTypeRegistryEntry[]>([]);
  const [templates, setTemplates] = useState<DealCardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Provider for cache invalidation
  const { refetch: refetchProvider } = useDealCardConfig();
  
  // Draft persistence
  const { getDraft, setDraft, discardDraft, hasDraft } = useDealCardDrafts();
  const isInitializing = useRef(true);
  // CR320: Hydration guard to prevent localStorage write-loop
  const isHydratingRef = useRef(false);
  // CR307 D4: Track previous deal type to detect type changes
  const prevSelectedType = useRef<string | null>(null);
  // CR307 D4: Track if we have dirty edits (prevents hydration while dirty)
  const isHydrationBlocked = useRef(false);
  // CR312: Request nonce to prevent stale fetch overwrites
  const requestNonce = useRef(0);
  // CR320: Last state change reason for debug strip
  const [lastStateChangeReason, setLastStateChangeReason] = useState<StateChangeReason>("INIT");
  
  // CR325: Stability mode (disables background refetch/auto-hydrate)
  const {
    stabilityMode,
    toggleStabilityMode,
    isRefetchAllowed,
    triggerManualRefresh,
  } = useStabilityMode(true);
  
  // CR325: Track previous dirty state for dirty_transition events
  const prevDirtyRef = useRef<{ template: boolean; ribbon: boolean } | null>(null);
  
  // The selected type is from URL params
  const selectedType = deal_type_key?.toUpperCase() || "BONUS";
  
  // Working state
  const [draftRibbon, setDraftRibbon] = useState<RibbonDraft>({
    ribbon_text: "BONUS",
    ribbon_theme: "blue",
    is_enabled: true,
  });
  const [draftHeader, setDraftHeader] = useState<HeaderComposition>(DEFAULT_HEADER_COMPOSITION);
  const [draftSections, setDraftSections] = useState<ExtendedSectionToggles>(DEFAULT_EXTENDED_SECTIONS);
  const [draftLabels, setDraftLabels] = useState<CardTemplateJson["labels"]>({});
  const [draftStyling, setDraftStyling] = useState<CardStyling>(DEFAULT_CARD_STYLING);
  const [draftSectionOrder, setDraftSectionOrder] = useState<string[]>([...DEFAULT_SECTION_ORDER]);
  const [themePreset, setThemePreset] = useState<"light" | "dark" | "custom">("light");
  const [draftSurfaceBlockToggles, setDraftSurfaceBlockToggles] = useState<{
    primary: Record<string, boolean>;
    details: Record<string, boolean>;
  }>({ primary: {}, details: {} });
  const [draftSurfaceBlockOrder, setDraftSurfaceBlockOrder] = useState<{
    primary: string[];
    details: string[];
  }>({ primary: [], details: [] });
  const [draftComponents, setDraftComponents] = useState<ComponentsConfig>({});
  const [draftPromoBadges, setDraftPromoBadges] = useState<PromoBadgesConfig>({ ...DEFAULT_PROMO_BADGES_CONFIG });
  const [draftDetailsContent, setDraftDetailsContent] = useState<DetailsContentConfig>({ ...DEFAULT_DETAILS_CONTENT_CONFIG });
  
  // CR307: Inspector panel state
  const [showInspector, setShowInspector] = useState(false);
  
  // Saved state for dirty tracking
  const [savedRibbon, setSavedRibbon] = useState<RibbonDraft | null>(null);
  const [savedTemplate, setSavedTemplate] = useState<ReturnType<typeof extractSavedTemplate> | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  // Templates by deal type key
  const templatesByDealTypeKey = useMemo(() => {
    const map = new Map<string, CardTemplateJson>();
    for (const t of templates) {
      map.set(t.deal_type_key, t.template_json);
    }
    return map;
  }, [templates]);

  // Fetch data
  const fetchData = useCallback(async (nonce?: number) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "GET",
      });
      
      if (error) {
        console.error("[TemplateEditorPage] Fetch error:", error);
        toast.error("Failed to load deal card config");
        return;
      }
      
      // CR312: Check if this fetch is still valid (not superseded by newer request)
      if (nonce !== undefined && nonce !== requestNonce.current) {
        logEvt({ type: "apply_ignored_stale", nonce, note: `expected=${requestNonce.current} got=${nonce}` });
        return;
      }
      
      logEvt({ type: "apply_registry", nonce, note: `reg=${(data.registry||[]).length} tpl=${(data.templates||[]).length}` });
      
      setRegistry(data.registry || []);
      setTemplates(data.templates || []);
    } catch (err) {
      console.error("[TemplateEditorPage] Unexpected error:", err);
      toast.error("Failed to load deal card config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CR322: Pass current nonce so initial fetch can be invalidated if type changes before it completes
    const nonce = requestNonce.current;
    fetchData(nonce);
  }, [fetchData]);

  // Hydrate state when data loads
  // CR307 D4: Only hydrate on deal type change, variant change, or explicit discard
  // CR320: Use isHydratingRef to prevent localStorage write-loop
  useEffect(() => {
    if (loading) return;
    
    // CR325: Block background hydration when stability mode is ON (after initial)
    if (stabilityMode && prevSelectedType.current === selectedType && prevSelectedType.current !== null) {
      // Same type, not first load, stability mode on → skip
      return;
    }
    
    // CR307 D4: Block hydration if dirty (unless deal type changed or first load)
    const dealTypeChanged = prevSelectedType.current !== null && prevSelectedType.current !== selectedType;
    const isFirstLoad = prevSelectedType.current === null;
    
    // CR320: Increment nonce on type switch to invalidate in-flight fetches
    if (dealTypeChanged) {
      requestNonce.current++;
      setLastStateChangeReason("TYPE_SWITCH");
      logEvt({ type: "type_switch", dealTypeKey: selectedType, nonce: requestNonce.current });
    }
    
    // Update previous type tracker
    prevSelectedType.current = selectedType;
    
    // If we have dirty changes and deal type didn't change, skip hydration
    if (isHydrationBlocked.current && !dealTypeChanged && !isFirstLoad) {
      console.log("[CR320] Skipping hydration while dirty");
      return;
    }
    
    // CR320: Set hydration guard
    isHydratingRef.current = true;
    
    // CR325: Log hydration start
    logEvt({ type: "hydrate_start", dealTypeKey: selectedType, nonce: requestNonce.current, hydrating: true });
    
    const regEntry = registry.find(r => r.deal_type_key === selectedType);
    const template = templates.find(t => t.deal_type_key === selectedType);
    
    const saved = {
      ribbon: extractSavedRibbon(regEntry, selectedType),
      ...extractSavedTemplate(template),
    };
    
    setSavedRibbon(saved.ribbon);
    setSavedTemplate({
      header: saved.header,
      sections: saved.sections,
      labels: saved.labels,
      styling: saved.styling,
      sectionOrder: saved.sectionOrder,
      themePreset: saved.themePreset,
      surfaceBlockToggles: saved.surfaceBlockToggles,
      surfaceBlockOrder: saved.surfaceBlockOrder,
      components: saved.components,
      promoBadges: saved.promoBadges,
      detailsContent: saved.detailsContent,
    });
    
    // Check for persisted draft
    const existingDraft = getDraft(selectedType);
    
    // CR320: Auto-clear drafts that aren't actually dirty
    if (existingDraft) {
      const draftIsDirty = 
        !stableEquals(existingDraft.ribbon, saved.ribbon) ||
        !stableEquals(existingDraft.header, saved.header) ||
        !stableEquals(existingDraft.sections, saved.sections) ||
        !stableEquals(existingDraft.labels, saved.labels) ||
        !stableEquals(existingDraft.styling, saved.styling) ||
        !stableEquals(existingDraft.sectionOrder, saved.sectionOrder) ||
        !stableEquals(existingDraft.surfaceBlockToggles || saved.surfaceBlockToggles, saved.surfaceBlockToggles) ||
        !stableEquals(existingDraft.surfaceBlockOrder || saved.surfaceBlockOrder, saved.surfaceBlockOrder) ||
        !stableEquals(existingDraft.components || saved.components, saved.components) ||
        !stableEquals(existingDraft.promoBadges || saved.promoBadges, saved.promoBadges) ||
        !stableEquals(existingDraft.detailsContent || saved.detailsContent, saved.detailsContent) ||
        existingDraft.themePreset !== saved.themePreset;
      
      if (draftIsDirty) {
        // Draft has real changes — apply it
        logEvt({ type: "apply_template_draft", dealTypeKey: selectedType, hydrating: true, note: "from localStorage" });
        setDraftRibbon(existingDraft.ribbon);
        setDraftHeader(existingDraft.header);
        setDraftSections(existingDraft.sections);
        setDraftLabels(existingDraft.labels);
        setDraftStyling(existingDraft.styling);
        setDraftSectionOrder(existingDraft.sectionOrder);
        setThemePreset(existingDraft.themePreset);
        setDraftSurfaceBlockToggles(existingDraft.surfaceBlockToggles || saved.surfaceBlockToggles);
        setDraftSurfaceBlockOrder(existingDraft.surfaceBlockOrder || saved.surfaceBlockOrder);
        setDraftComponents(existingDraft.components || saved.components);
        setDraftPromoBadges(existingDraft.promoBadges || saved.promoBadges);
        setDraftDetailsContent(existingDraft.detailsContent || saved.detailsContent);
        setLastStateChangeReason("HYDRATE_DRAFT");
      } else {
        // Draft matches saved — auto-clear stale draft
        logEvt({ type: "draft_clear", dealTypeKey: selectedType, note: "auto-clear stale draft" });
        discardDraft(selectedType);
        setDraftRibbon(saved.ribbon);
        setDraftHeader(saved.header);
        setDraftSections(saved.sections);
        setDraftLabels(saved.labels);
        setDraftStyling(saved.styling);
        setDraftSectionOrder(saved.sectionOrder);
        setThemePreset(saved.themePreset);
        setDraftSurfaceBlockToggles(saved.surfaceBlockToggles);
        setDraftSurfaceBlockOrder(saved.surfaceBlockOrder);
        setDraftComponents(saved.components);
        setDraftPromoBadges(saved.promoBadges);
        setDraftDetailsContent(saved.detailsContent);
        setLastStateChangeReason("HYDRATE_NO_DRAFT");
      }
    } else {
      setDraftRibbon(saved.ribbon);
      setDraftHeader(saved.header);
      setDraftSections(saved.sections);
      setDraftLabels(saved.labels);
      setDraftStyling(saved.styling);
      setDraftSectionOrder(saved.sectionOrder);
      setThemePreset(saved.themePreset);
      setDraftSurfaceBlockToggles(saved.surfaceBlockToggles);
      setDraftSurfaceBlockOrder(saved.surfaceBlockOrder);
      setDraftComponents(saved.components);
      setDraftPromoBadges(saved.promoBadges);
      setDraftDetailsContent(saved.detailsContent);
      setLastStateChangeReason(isFirstLoad ? "FETCH_SAVED_SUCCESS" : "HYDRATE_NO_DRAFT");
    }
    
    isInitializing.current = false;
    
    // CR325: Log hydration commit
    logEvt({
      type: "hydrate_commit",
      dealTypeKey: selectedType,
      nonce: requestNonce.current,
      hydrating: true,
      note: existingDraft ? "had draft" : "no draft",
    });
    
    // CR322: Do NOT clear isHydratingRef here. It will be cleared by the
    // persist effect on its first run after hydration. This ensures the guard
    // survives through React's batched re-render cycle and prevents the
    // persist effect from writing to localStorage during hydration.
  }, [selectedType, registry, templates, loading, getDraft, discardDraft, stabilityMode]);

  // Persist draft to localStorage
  // CR322: Hydration-aware + debounced draft persistence
  // - Clears isHydratingRef on first run after hydration (ensures guard survives React render cycle)
  // - Debounces localStorage writes by 250ms to prevent rapid fire during state transitions
  useEffect(() => {
    if (isInitializing.current || !savedRibbon || !savedTemplate) return;
    
    // CR322: If hydrating, clear the guard and skip this persist cycle.
    // This is the ONLY place isHydratingRef is cleared, guaranteeing the guard
    // lasts through React's batched re-render after hydration state changes.
    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      logEvt({ type: "draft_persist_skipped_hydrating", dealTypeKey: selectedType, hydrating: true });
      return;
    }
    
    // CR322: Debounce localStorage writes (250ms) to prevent rapid fire
    const timer = setTimeout(() => {
      const isDirty = 
        !stableEquals(draftRibbon, savedRibbon) ||
        !stableEquals(draftHeader, savedTemplate.header) ||
        !stableEquals(draftSections, savedTemplate.sections) ||
        !stableEquals(draftLabels, savedTemplate.labels) ||
        !stableEquals(draftStyling, savedTemplate.styling) ||
        !stableEquals(draftSectionOrder, savedTemplate.sectionOrder) ||
        !stableEquals(draftSurfaceBlockToggles, savedTemplate.surfaceBlockToggles) ||
        !stableEquals(draftSurfaceBlockOrder, savedTemplate.surfaceBlockOrder) ||
        !stableEquals(draftComponents, savedTemplate.components) ||
        !stableEquals(draftPromoBadges, savedTemplate.promoBadges) ||
        !stableEquals(draftDetailsContent, savedTemplate.detailsContent) ||
        themePreset !== savedTemplate.themePreset;
      
      if (isDirty) {
        logEvt({ type: "draft_persist_template", dealTypeKey: selectedType, hydrating: false, templateDirty: true });
        const currentDraft: DealTypeDraft = {
          ribbon: draftRibbon,
          header: draftHeader,
          sections: draftSections,
          labels: draftLabels,
          styling: draftStyling,
          sectionOrder: draftSectionOrder,
          themePreset,
          surfaceBlockToggles: draftSurfaceBlockToggles,
          surfaceBlockOrder: draftSurfaceBlockOrder,
          components: draftComponents,
          promoBadges: draftPromoBadges,
          detailsContent: draftDetailsContent,
          savedAt: new Date().toISOString(),
        };
        setDraft(selectedType, currentDraft);
      } else {
        discardDraft(selectedType);
      }
    }, 250);
    
    return () => clearTimeout(timer);
  }, [
    draftRibbon, draftHeader, draftSections, draftLabels, draftStyling, draftSectionOrder, themePreset,
    draftSurfaceBlockToggles, draftSurfaceBlockOrder, draftComponents, draftPromoBadges, draftDetailsContent,
    savedRibbon, savedTemplate, selectedType, setDraft, discardDraft
  ]);

  // Push to preview store
  useEffect(() => {
    if (isInitializing.current) return;
    
    previewConfigStore.setDealCardOverrides(selectedType, {
      registry: {
        ribbon_text: draftRibbon.ribbon_text,
        ribbon_theme: draftRibbon.ribbon_theme,
        is_enabled: draftRibbon.is_enabled,
      },
      template: {
        sections: draftSections,
        labels: draftLabels,
        header: draftHeader,
        extended_sections: draftSections,
        styling: draftStyling,
        section_order: draftSectionOrder,
        theme_preset: themePreset,
        surfaceOverrides: {
          primary: {
            blockToggles: draftSurfaceBlockToggles.primary,
            blockOrder: draftSurfaceBlockOrder.primary.length > 0 ? draftSurfaceBlockOrder.primary : undefined,
          },
          details: {
            blockToggles: draftSurfaceBlockToggles.details,
            blockOrder: draftSurfaceBlockOrder.details.length > 0 ? draftSurfaceBlockOrder.details : undefined,
          },
        },
        components: draftComponents,
        promo_badges: draftPromoBadges,
        details_content: draftDetailsContent,
      },
    });
  }, [
    selectedType, draftRibbon, draftHeader, draftSections, draftLabels, draftStyling, 
    draftSectionOrder, themePreset, draftSurfaceBlockToggles, draftSurfaceBlockOrder,
    draftComponents, draftPromoBadges, draftDetailsContent
  ]);

  // Dirty tracking
  // CR312: Use stable comparison to prevent phantom dirty from key ordering
  const isRibbonDirty = useMemo((): boolean => {
    if (!savedRibbon) return false;
    const dirty = !stableEquals(draftRibbon, savedRibbon);
    return dirty;
  }, [draftRibbon, savedRibbon]);

  // CR312: Use stable comparison for all template fields
  const isTemplateDirty = useMemo((): boolean => {
    if (!savedTemplate) return false;
    const dirty = (
      !stableEquals(draftHeader, savedTemplate.header) ||
      !stableEquals(draftSections, savedTemplate.sections) ||
      !stableEquals(draftLabels, savedTemplate.labels) ||
      !stableEquals(draftStyling, savedTemplate.styling) ||
      !stableEquals(draftSectionOrder, savedTemplate.sectionOrder) ||
      !stableEquals(draftSurfaceBlockToggles, savedTemplate.surfaceBlockToggles) ||
      !stableEquals(draftSurfaceBlockOrder, savedTemplate.surfaceBlockOrder) ||
      !stableEquals(draftComponents, savedTemplate.components) ||
      !stableEquals(draftPromoBadges, savedTemplate.promoBadges) ||
      !stableEquals(draftDetailsContent, savedTemplate.detailsContent) ||
      themePreset !== savedTemplate.themePreset
    );
    return dirty;
  }, [draftHeader, draftSections, draftLabels, draftStyling, draftSectionOrder, 
      draftSurfaceBlockToggles, draftSurfaceBlockOrder, draftComponents, 
      draftPromoBadges, draftDetailsContent, themePreset, savedTemplate]);

  const hasUnsavedChanges = isRibbonDirty || isTemplateDirty;
  
  // CR320: Compute stable hashes for debug strip (must be before dirty_transition effect)
  const savedHashStr = useMemo(() => {
    if (!savedRibbon || !savedTemplate) return "------";
    return stableHash({ ribbon: savedRibbon, ...savedTemplate });
  }, [savedRibbon, savedTemplate]);
  
  const effectiveHashStr = useMemo(() => {
    return stableHash({
      ribbon: draftRibbon,
      header: draftHeader,
      sections: draftSections,
      labels: draftLabels,
      styling: draftStyling,
      sectionOrder: draftSectionOrder,
      themePreset,
      surfaceBlockToggles: draftSurfaceBlockToggles,
      surfaceBlockOrder: draftSurfaceBlockOrder,
      components: draftComponents,
      promoBadges: draftPromoBadges,
      detailsContent: draftDetailsContent,
    });
  }, [draftRibbon, draftHeader, draftSections, draftLabels, draftStyling,
      draftSectionOrder, themePreset, draftSurfaceBlockToggles, draftSurfaceBlockOrder,
      draftComponents, draftPromoBadges, draftDetailsContent]);
  
  // CR325: Log dirty transitions + R5 violation guard
  useEffect(() => {
    const prev = prevDirtyRef.current;
    const curr = { template: isTemplateDirty, ribbon: isRibbonDirty };
    if (prev && (prev.template !== curr.template || prev.ribbon !== curr.ribbon)) {
      logEvt({
        type: "dirty_transition",
        dealTypeKey: selectedType,
        templateDirty: curr.template,
        savedHash: savedHashStr.slice(0, 8),
        effectiveHash: effectiveHashStr.slice(0, 8),
        note: `tpl:${prev.template}→${curr.template} rib:${prev.ribbon}→${curr.ribbon}`,
      });
      
      // CR325 R5: Violation guard — dirty changed but hashes didn't?
      if (curr.template !== prev.template && savedHashStr === effectiveHashStr) {
        logEvt({
          type: "dirty_violation_template_from_non_template_domain",
          dealTypeKey: selectedType,
          savedHash: savedHashStr.slice(0, 8),
          effectiveHash: effectiveHashStr.slice(0, 8),
          note: "VIOLATION: dirty changed without hash change",
        });
      }
    }
    prevDirtyRef.current = curr;
  }, [isTemplateDirty, isRibbonDirty, selectedType, savedHashStr, effectiveHashStr]);
  
  // CR320: Source mode derived from dirty state
  const sourceMode: EditorSourceMode = hasUnsavedChanges ? "draft" : "db";
  
  // CR320: isDirtyForType — replaces hasDraft for dropdown badges
  // Returns true only if deal type has a draft that is genuinely dirty (not just exists)
  const isDirtyForType = useCallback((dealTypeKey: string): boolean => {
    if (dealTypeKey === selectedType) {
      // For the active type, use computed dirty state directly
      return hasUnsavedChanges;
    }
    // For inactive types, check if a draft exists
    const draft = getDraft(dealTypeKey);
    if (!draft) return false;
    
    // Check if the draft is actually different from saved
    const regEntry = registry.find(r => r.deal_type_key === dealTypeKey);
    const template = templates.find(t => t.deal_type_key === dealTypeKey);
    const saved = {
      ribbon: extractSavedRibbon(regEntry, dealTypeKey),
      ...extractSavedTemplate(template),
    };
    
    return (
      !stableEquals(draft.ribbon, saved.ribbon) ||
      !stableEquals(draft.header, saved.header) ||
      !stableEquals(draft.sections, saved.sections) ||
      !stableEquals(draft.labels, saved.labels) ||
      !stableEquals(draft.styling, saved.styling) ||
      !stableEquals(draft.sectionOrder, saved.sectionOrder) ||
      !stableEquals(draft.surfaceBlockToggles || saved.surfaceBlockToggles, saved.surfaceBlockToggles) ||
      !stableEquals(draft.surfaceBlockOrder || saved.surfaceBlockOrder, saved.surfaceBlockOrder) ||
      !stableEquals(draft.components || saved.components, saved.components) ||
      !stableEquals(draft.promoBadges || saved.promoBadges, saved.promoBadges) ||
      !stableEquals(draft.detailsContent || saved.detailsContent, saved.detailsContent) ||
      draft.themePreset !== saved.themePreset
    );
  }, [selectedType, hasUnsavedChanges, getDraft, registry, templates]);
  
  // CR307 D4: Update hydration block ref whenever dirty state changes
  useEffect(() => {
    isHydrationBlocked.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // CR311: Force-sync draft state to saved state (used after successful save)
  const syncDraftToSaved = useCallback(() => {
    if (!savedRibbon || !savedTemplate) return;
    
    setDraftRibbon(savedRibbon);
    setDraftHeader(savedTemplate.header);
    setDraftSections(savedTemplate.sections);
    setDraftLabels(savedTemplate.labels);
    setDraftStyling(savedTemplate.styling);
    setDraftSectionOrder(savedTemplate.sectionOrder);
    setThemePreset(savedTemplate.themePreset);
    setDraftSurfaceBlockToggles(savedTemplate.surfaceBlockToggles);
    setDraftSurfaceBlockOrder(savedTemplate.surfaceBlockOrder);
    setDraftComponents(savedTemplate.components);
    setDraftPromoBadges(savedTemplate.promoBadges);
    setDraftDetailsContent(savedTemplate.detailsContent);
  }, [savedRibbon, savedTemplate]);

  // Preview key for stability
  const draftConfig = useMemo(() => ({
    ribbon: draftRibbon,
    sections: draftSections,
    styling: draftStyling,
    header: draftHeader,
    labels: draftLabels,
    sectionOrder: draftSectionOrder,
    themePreset,
  }), [draftRibbon, draftSections, draftStyling, draftHeader, draftLabels, draftSectionOrder, themePreset]);

  const { previewKey, configHash: draftHash, isDebouncing } = useStablePreviewKey(draftConfig, {
    prefix: selectedType,
    debounceMs: 150,
  });

  // Handlers
  const handleRibbonChange = useCallback((update: Partial<RibbonDraft>) => {
    setDraftRibbon(prev => ({ ...prev, ...update }));
  }, []);

  const handleHeaderChange = useCallback((update: Partial<HeaderComposition>) => {
    setDraftHeader(prev => ({ ...prev, ...update }));
  }, []);

  const handleSectionToggle = useCallback((section: keyof ExtendedSectionToggles, value: boolean) => {
    setDraftSections(prev => ({ ...prev, [section]: value }));
  }, []);

  const handleLabelChange = useCallback((key: string, value: string) => {
    setDraftLabels(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleStylingChange = useCallback((update: Partial<CardStyling>) => {
    setDraftStyling(prev => ({ ...prev, ...update }));
    if ('showMerchantName' in update) {
      setDraftSections(prev => ({ ...prev, merchant_name: update.showMerchantName ?? true }));
    }
  }, []);

  const handleSectionOrderChange = useCallback((newOrder: string[]) => {
    setDraftSectionOrder(newOrder);
  }, []);

  const handleThemeChange = useCallback((theme: "light" | "dark" | "custom") => {
    setThemePreset(theme);
  }, []);

  const handleSurfaceBlockToggle = useCallback((surface: 'primary' | 'details', blockId: string, enabled: boolean) => {
    setDraftSurfaceBlockToggles(prev => ({
      ...prev,
      [surface]: { ...prev[surface], [blockId]: enabled },
    }));
  }, []);

  const handleSurfaceBlockOrderChange = useCallback((surface: 'primary' | 'details', newOrder: string[]) => {
    setDraftSurfaceBlockOrder(prev => ({ ...prev, [surface]: newOrder }));
  }, []);

  const handleComponentsChange = useCallback((update: Partial<ComponentsConfig>) => {
    setDraftComponents(prev => ({ ...prev, ...update }));
  }, []);

  const handlePromoBadgesChange = useCallback((config: PromoBadgesConfig) => {
    setDraftPromoBadges(config);
  }, []);

  const handleDetailsContentChange = useCallback((config: DetailsContentConfig) => {
    setDraftDetailsContent(config);
  }, []);
  
  // CR307: Build current overrides from draft state
  const currentOverrides = useMemo((): Record<string, unknown> => {
    const { ribbon, debug_strip, merchant_category_chips, confidence_indicator, claim_button, ...coreSections } = draftSections;
    
    return {
      schemaVersion: 2,
      variantKey: "standard_v2",
      labels: draftLabels,
      header: draftHeader,
      styling: draftStyling,
      section_order: draftSectionOrder,
      theme_preset: themePreset,
      sections: coreSections,
      extended_sections: {
        ribbon,
        debug_strip,
        merchant_category_chips,
        confidence_indicator,
        claim_button,
      },
      surfaceOverrides: {
        primary: {
          blockToggles: draftSurfaceBlockToggles.primary,
          ...(draftSurfaceBlockOrder.primary.length > 0 && { blockOrder: draftSurfaceBlockOrder.primary }),
        },
        details: {
          blockToggles: draftSurfaceBlockToggles.details,
          ...(draftSurfaceBlockOrder.details.length > 0 && { blockOrder: draftSurfaceBlockOrder.details }),
        },
      },
      components: draftComponents,
      promo_badges: draftPromoBadges,
      details_content: draftDetailsContent,
    };
  }, [draftSections, draftLabels, draftHeader, draftStyling, draftSectionOrder, themePreset,
      draftSurfaceBlockToggles, draftSurfaceBlockOrder, draftComponents, draftPromoBadges, draftDetailsContent]);
  
  // CR307: Get current template_json for inspector baseline
  const currentTemplateJson = useMemo((): Record<string, unknown> | null => {
    const template = templates.find(t => t.deal_type_key === selectedType);
    return (template?.template_json as unknown as Record<string, unknown>) || null;
  }, [templates, selectedType]);
  
  // CR307: Inspector action handlers
  const handleResetOverridePath = useCallback((path: string) => {
    console.log("[CR307] Reset override at path:", path);
    // Reset by removing from current overrides
    const newOverrides = removeOverrideAtPath(currentOverrides, path);
    // Re-hydrate draft state from template baseline for this path
    toast.info(`Reset override at ${path}`);
  }, [currentOverrides]);
  
  const handleCopyToOverride = useCallback((path: string, value: unknown) => {
    console.log("[CR307] Copy to override:", path, value);
    const newOverrides = applyOverrideEdit(currentOverrides, path, value);
    toast.info(`Copied value to override at ${path}`);
  }, [currentOverrides]);

  // Discard changes
  // CR322: Set hydration guard during discard to prevent persist effect from writing stale draft
  const discardChanges = useCallback(async () => {
    logEvt({ type: "discard", dealTypeKey: selectedType, nonce: requestNonce.current });
    setLoading(true);
    isHydratingRef.current = true; // CR322: Guard against persist during state reset
    
    try {
      discardDraft(selectedType);
      
      // CR322: Increment nonce to invalidate any in-flight fetches
      requestNonce.current++;
      const currentNonce = requestNonce.current;
      
      const { data, error } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "GET",
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      // CR322: Check nonce — if user switched types during discard fetch, abort
      if (currentNonce !== requestNonce.current) {
        console.log("[CR322] Discard fetch superseded, ignoring response");
        return;
      }
      
      const freshRegistry = data.registry || [];
      const freshTemplates = data.templates || [];
      
      setRegistry(freshRegistry);
      setTemplates(freshTemplates);
      
      const regEntry = freshRegistry.find((r: DealTypeRegistryEntry) => r.deal_type_key === selectedType);
      const template = freshTemplates.find((t: DealCardTemplate) => t.deal_type_key === selectedType);
      
      const freshSaved = {
        ribbon: extractSavedRibbon(regEntry, selectedType),
        ...extractSavedTemplate(template),
      };
      
      setSavedRibbon(freshSaved.ribbon);
      setSavedTemplate(freshSaved);
      
      setDraftRibbon({ ...freshSaved.ribbon });
      setDraftHeader({ ...freshSaved.header });
      setDraftSections({ ...freshSaved.sections });
      setDraftLabels({ ...freshSaved.labels });
      setDraftStyling({ ...freshSaved.styling });
      setDraftSectionOrder([...freshSaved.sectionOrder]);
      setThemePreset(freshSaved.themePreset);
      setDraftSurfaceBlockToggles(freshSaved.surfaceBlockToggles);
      setDraftSurfaceBlockOrder(freshSaved.surfaceBlockOrder);
      setDraftComponents(freshSaved.components);
      setDraftPromoBadges(freshSaved.promoBadges);
      setDraftDetailsContent(freshSaved.detailsContent);
      // CR320: Set state change reason
      setLastStateChangeReason("DISCARD_RESTORE_SAVED");
      
      // CR322: Unblock hydration
      isHydrationBlocked.current = false;
      
      toast.info("Changes discarded - restored to last saved version");
    } catch (err) {
      console.error("[TemplateEditorPage] Discard error:", err);
      toast.error("Failed to discard changes");
    } finally {
      setLoading(false);
    }
  }, [selectedType, discardDraft]);

  // Save
  const saveAll = useCallback(async () => {
    logEvt({ type: "save_template_start", dealTypeKey: selectedType, nonce: requestNonce.current, templateDirty: isTemplateDirty });
    setSaving(true);
    let registrySaved = false;
    let templateSaved = false;
    // CR312: Track response data for immediate baseline update
    let savedTemplateFromResponse: DealCardTemplate | undefined;
    let savedRegistryFromResponse: DealTypeRegistryEntry | undefined;
    
    try {
      if (isRibbonDirty) {
        const { data: regResponse, error: regError } = await supabase.functions.invoke("command-center-deal-cards", {
          method: "POST",
          body: {
            _action: "update_registry",
            deal_type_key: selectedType,
            ribbon_text: draftRibbon.ribbon_text,
            ribbon_theme: draftRibbon.ribbon_theme,
            is_enabled: draftRibbon.is_enabled,
          },
        });
        
        if (regError) {
          throw new Error(`Registry save failed: ${regError.message}`);
        }
        registrySaved = true;
        
        // CR312: Capture saved registry for immediate baseline
        if (regResponse?.registry) {
          savedRegistryFromResponse = regResponse.registry;
        }
      }
      
      if (isTemplateDirty) {
        const { ribbon, debug_strip, merchant_category_chips, confidence_indicator, claim_button, ...coreSections } = draftSections;
        
        // CR307: Build overrides payload (all edits go to overrides_json)
        const overridesPayload: Record<string, unknown> = {
          schemaVersion: 2,
          variantKey: "standard_v2",
          labels: draftLabels,
          header: draftHeader,
          styling: draftStyling || {},
          sections: coreSections,
          section_order: draftSectionOrder.length > 0 ? draftSectionOrder : undefined,
          theme_preset: themePreset || undefined,
          surfaceOverrides: {
            primary: { 
              blockToggles: draftSurfaceBlockToggles.primary,
              ...(draftSurfaceBlockOrder.primary.length > 0 && { blockOrder: draftSurfaceBlockOrder.primary }),
            },
            details: { 
              blockToggles: draftSurfaceBlockToggles.details,
              ...(draftSurfaceBlockOrder.details.length > 0 && { blockOrder: draftSurfaceBlockOrder.details }),
            },
          },
          extended_sections: {
            ribbon,
            debug_strip,
            merchant_category_chips,
            confidence_indicator,
            claim_button,
          },
          ...(Object.keys(draftComponents).length > 0 && { components: draftComponents }),
          ...(draftPromoBadges && { promo_badges: draftPromoBadges }),
          ...(draftDetailsContent && { details_content: draftDetailsContent }),
        };
        
        // CR307: Client-side sanitization before save
        const sanitizedOverrides = prepareOverridesForSave(overridesPayload);
        
        console.log("[CR307][TemplateEditorPage] Save to overrides_json:", {
          deal_type_key: selectedType,
          "blockToggles.ribbon": draftSurfaceBlockToggles.primary?.ribbon,
          topLevelKeys: Object.keys(sanitizedOverrides),
        });
        
        // CR307: Use update_overrides action (overrides-first editing)
        // CR312: Capture response to use for immediate baseline update
        const { data: tplResponse, error: tplError } = await supabase.functions.invoke("command-center-deal-cards", {
          method: "POST",
          body: {
            _action: "update_overrides",
            deal_type_key: selectedType,
            overrides_json: sanitizedOverrides,
          },
        });
        
        if (tplError) {
          throw new Error(`Template save failed: ${tplError.message}`);
        }
        templateSaved = true;
        
        // CR312: If server returned saved template, use it directly for baseline
        if (tplResponse?.template) {
          console.log("[CR312] Using save response directly for baseline update");
          savedTemplateFromResponse = tplResponse.template;
        }
      }
      
      const parts = [];
      if (registrySaved) parts.push("ribbon");
      if (templateSaved) parts.push("template");
      
      if (parts.length > 0) {
        toast.success(`Saved ${parts.join(" + ")} for ${selectedType}`);
        setLastSavedAt(new Date().toISOString());
      } else {
        toast.info("No changes to save");
        return;
      }
      
      // CR312: Re-baseline immediately using save response (no second fetch race)
      // Step 1: Build new baseline from save responses
      let freshSaved: ReturnType<typeof extractSavedTemplate> & { ribbon: RibbonDraft };
      
      if (savedTemplateFromResponse) {
        // Use template from save response directly
        const template = savedTemplateFromResponse as DealCardTemplate;
        const regEntry = savedRegistryFromResponse as DealTypeRegistryEntry | undefined 
          || registry.find(r => r.deal_type_key === selectedType);
        
        freshSaved = {
          ribbon: extractSavedRibbon(regEntry, selectedType),
          ...extractSavedTemplate(template),
        };
        
        // Update templates array with saved template
        setTemplates(prev => prev.map(t => 
          t.deal_type_key === selectedType ? template : t
        ));
      } else {
        // Fallback: use current draft as baseline (edge case)
        freshSaved = {
          ribbon: savedRegistryFromResponse 
            ? extractSavedRibbon(savedRegistryFromResponse as DealTypeRegistryEntry, selectedType) 
            : draftRibbon,
          header: draftHeader,
          sections: draftSections,
          labels: draftLabels,
          styling: draftStyling,
          sectionOrder: draftSectionOrder,
          themePreset,
          surfaceBlockToggles: draftSurfaceBlockToggles,
          surfaceBlockOrder: draftSurfaceBlockOrder,
          components: draftComponents,
          promoBadges: draftPromoBadges,
          detailsContent: draftDetailsContent,
        };
      }
      
      // Step 2: Update registry if it was saved
      if (savedRegistryFromResponse) {
        setRegistry(prev => prev.map(r => 
          r.deal_type_key === selectedType ? savedRegistryFromResponse as DealTypeRegistryEntry : r
        ));
        freshSaved.ribbon = extractSavedRibbon(savedRegistryFromResponse as DealTypeRegistryEntry, selectedType);
      }
      
      // Step 3: Update saved baseline (single source of truth)
      // CR322: Set hydration guard before snapping draft to prevent persist effect loop
      isHydratingRef.current = true;
      setSavedRibbon(freshSaved.ribbon);
      setSavedTemplate(freshSaved);
      
      // Step 4: Snap draft to match saved (clears dirty indicators instantly)
      setDraftRibbon({ ...freshSaved.ribbon });
      setDraftHeader({ ...freshSaved.header });
      setDraftSections({ ...freshSaved.sections });
      setDraftLabels({ ...freshSaved.labels });
      setDraftStyling({ ...freshSaved.styling });
      setDraftSectionOrder([...freshSaved.sectionOrder]);
      setThemePreset(freshSaved.themePreset);
      setDraftSurfaceBlockToggles({ ...freshSaved.surfaceBlockToggles });
      setDraftSurfaceBlockOrder({ ...freshSaved.surfaceBlockOrder });
      setDraftComponents({ ...freshSaved.components });
      setDraftPromoBadges({ ...freshSaved.promoBadges });
      setDraftDetailsContent({ ...freshSaved.detailsContent });
      
      // Step 5: Clear localStorage draft AFTER baseline update
      discardDraft(selectedType);
      
      // Step 6: Unblock hydration
      isHydrationBlocked.current = false;
      
      // Step 7: Increment nonce to invalidate any in-flight fetches
      requestNonce.current++;
      
      // CR320: Set state change reason
      setLastStateChangeReason("SAVE_SUCCESS_CLEAR_DRAFT");
      
      // CR325: Log save success
      logEvt({
        type: "save_template_success",
        dealTypeKey: selectedType,
        nonce: requestNonce.current,
        templateDirty: false,
        note: `reg=${registrySaved} tpl=${templateSaved}`,
      });
      
      // Refresh provider cache
      await refetchProvider();
      
    } catch (err) {
      logEvt({ type: "save_template_fail", dealTypeKey: selectedType, note: String(err) });
      console.error("[TemplateEditorPage] Save error:", err);
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.error(`Save failed: ${msg}. Draft preserved.`);
    } finally {
      setSaving(false);
    }
  }, [
    isRibbonDirty, isTemplateDirty, selectedType, draftRibbon, draftHeader, draftSections, 
    draftLabels, draftStyling, draftSectionOrder, themePreset, draftSurfaceBlockToggles,
    draftSurfaceBlockOrder, draftComponents, draftPromoBadges, draftDetailsContent,
    discardDraft, fetchData, refetchProvider
  ]);

  // Create from default
  // CR322: Increment nonce + set hydration guard before fetch
  const handleCreateFromDefault = useCallback(async (dealTypeKey: string): Promise<boolean> => {
    try {
      const defaultTemplate = getDefaultTemplateForType(dealTypeKey);
      const sanitizedTemplate = sanitizeDealCardJsonForV2(defaultTemplate);
      
      const { error: insertError } = await supabase
        .from("deal_card_templates")
        .upsert([{
          deal_type_key: dealTypeKey.toUpperCase(),
          template_json: JSON.parse(JSON.stringify(sanitizedTemplate)) as Json,
          updated_at: new Date().toISOString(),
        }], {
          onConflict: "deal_type_key",
        });
      
      if (insertError) {
        console.error("[handleCreateFromDefault] Insert error:", insertError);
        toast.error("Failed to create template");
        return false;
      }
      
      toast.success(`Created template for ${dealTypeKey}`);
      // CR322: Set hydration guard + nonce before refetch
      isHydratingRef.current = true;
      requestNonce.current++;
      await fetchData(requestNonce.current);
      return true;
    } catch (err) {
      console.error("[handleCreateFromDefault] Error:", err);
      toast.error("Failed to create template");
      return false;
    }
  }, [fetchData]);

  // Duplicate from
  const handleDuplicateFrom = useCallback(async (
    sourceDealTypeKey: string, 
    targetDealTypeKey: string
  ): Promise<boolean> => {
    try {
      const sourceTemplate = templatesByDealTypeKey.get(sourceDealTypeKey.toUpperCase());
      if (!sourceTemplate) {
        toast.error(`Source template not found: ${sourceDealTypeKey}`);
        return false;
      }
      
      const sanitizedTemplate = sanitizeDealCardJsonForV2(sourceTemplate);
      
      const { error: insertError } = await supabase
        .from("deal_card_templates")
        .upsert([{
          deal_type_key: targetDealTypeKey.toUpperCase(),
          template_json: JSON.parse(JSON.stringify(sanitizedTemplate)) as Json,
          updated_at: new Date().toISOString(),
        }], {
          onConflict: "deal_type_key",
        });
      
      if (insertError) {
        console.error("[handleDuplicateFrom] Insert error:", insertError);
        toast.error("Failed to duplicate template");
        return false;
      }
      
      toast.success(`Duplicated template from ${sourceDealTypeKey} to ${targetDealTypeKey}`);
      // CR322: Set hydration guard + nonce before refetch
      isHydratingRef.current = true;
      requestNonce.current++;
      await fetchData(requestNonce.current);
      return true;
    } catch (err) {
      console.error("[handleDuplicateFrom] Error:", err);
      toast.error("Failed to duplicate template");
      return false;
    }
  }, [templatesByDealTypeKey, fetchData]);

  // Handle type change (navigate to different deal type)
  const handleTypeChange = useCallback((newType: string) => {
    navigate(`/admin/deal-cards/templates/${newType}/${variant_key || "standard_v2"}`);
  }, [navigate, variant_key]);

  // Loading state
  if (loading && registry.length === 0) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10" />
            <div className="space-y-2">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
          <Skeleton className="h-[600px] w-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-4">
        {/* Header with back button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin/deal-cards")}
              className="gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                Template Editor
                <Badge variant="outline" className="font-mono">
                  {selectedType}
                </Badge>
              </h1>
              <p className="text-sm text-muted-foreground">
                {variant_key || "standard_v2"}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* CR307: Inspector toggle */}
            <Button
              variant={showInspector ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setShowInspector(!showInspector)}
              className="gap-1"
              title="Toggle Source Inspector"
            >
              <Bug className="h-4 w-4" />
            </Button>
            {hasUnsavedChanges && (
              <Button
                variant="outline"
                size="sm"
                onClick={discardChanges}
                disabled={saving}
                className="gap-1"
              >
                <Undo2 className="h-4 w-4" />
                Discard
              </Button>
            )}
            <Button
              size="sm"
              onClick={saveAll}
              disabled={!hasUnsavedChanges || saving}
              className="gap-1"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        </div>

        {/* CR320: Debug strip for stability verification */}
        {searchParams.get("debug") === "1" && (
          <>
            <EditorStabilityDebugStrip
              dealTypeKey={selectedType}
              variantKey={variant_key || "standard_v2"}
              sourceMode={sourceMode}
              dirty={hasUnsavedChanges}
              savedHash={savedHashStr}
              effectiveHash={effectiveHashStr}
              lastStateChangeReason={lastStateChangeReason}
              hydratingNow={isHydratingRef.current}
              requestNonce={requestNonce.current}
              registryRibbonTheme={registry.find(r => r.deal_type_key === selectedType)?.ribbon_theme}
              dotThemeUsed={registry.find(r => r.deal_type_key === selectedType)?.ribbon_theme}
            />
            {/* CR325: Bounce Log Panel */}
            <BounceLogPanel
              stabilityMode={stabilityMode}
              onToggleStabilityMode={toggleStabilityMode}
              onManualRefresh={() => {
                triggerManualRefresh();
                isHydratingRef.current = true;
                requestNonce.current++;
                fetchData(requestNonce.current);
              }}
            />
          </>
        )}

        {/* Editor Core */}
        
        {/* CR307: Source Inspector Panel */}
        {showInspector && (
          <div className="border rounded-lg p-4 bg-muted/30">
            <OverrideSourceInspector
              dealTypeKey={selectedType}
              overrides={currentOverrides}
              template={currentTemplateJson}
              legacy={null}
              onResetPath={handleResetOverridePath}
              onCopyToOverride={handleCopyToOverride}
            />
          </div>
        )}
        
        <DealCardEditorCore
          registry={registry}
          templates={templates}
          templatesByDealTypeKey={templatesByDealTypeKey}
          selectedType={selectedType}
          onTypeChange={handleTypeChange}
          draftRibbon={draftRibbon}
          draftHeader={draftHeader}
          draftSections={draftSections}
          draftLabels={draftLabels}
          draftStyling={draftStyling}
          draftSectionOrder={draftSectionOrder}
          themePreset={themePreset}
          onRibbonChange={handleRibbonChange}
          onHeaderChange={handleHeaderChange}
          onSectionToggle={handleSectionToggle}
          onLabelChange={handleLabelChange}
          onStylingChange={handleStylingChange}
          onSectionOrderChange={handleSectionOrderChange}
          onThemeChange={handleThemeChange}
          draftSurfaceBlockToggles={draftSurfaceBlockToggles}
          onSurfaceBlockToggle={handleSurfaceBlockToggle}
          draftSurfaceBlockOrder={draftSurfaceBlockOrder}
          onSurfaceBlockOrderChange={handleSurfaceBlockOrderChange}
          draftComponents={draftComponents}
          onComponentsChange={handleComponentsChange}
          draftPromoBadges={draftPromoBadges}
          onPromoBadgesChange={handlePromoBadgesChange}
          draftDetailsContent={draftDetailsContent}
          onDetailsContentChange={handleDetailsContentChange}
          onSave={saveAll}
          onDiscard={discardChanges}
          onRefresh={fetchData}
          onCreateFromDefault={handleCreateFromDefault}
          onDuplicateFrom={handleDuplicateFrom}
          hasUnsavedChanges={hasUnsavedChanges}
          hasDraft={isDirtyForType}
          saving={saving}
          loading={loading}
          previewKey={previewKey}
          draftHash={draftHash}
          lastSavedAt={lastSavedAt}
          isDebouncing={isDebouncing}
          showDebug={searchParams.get("debug") === "1"}
        />
      </div>
    </AdminLayout>
  );
}
